import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import CCPopover from 'components/shared/DesignSystem/SlabTransferPortal/Floating/CCPopover';
import { baseButtonStyles } from 'components/shared/DesignSystem/shared/styles';
import ClearButton from './ClearButton';
import { tokens } from '@companycam/slab';

// #region styled-components
const getColor = (showFocusStyle, showActiveStyle) => {
  if (showFocusStyle) {
    return 'var(--cc_color_link_secondary)';
  } else if (showActiveStyle) {
    return 'white';
  } else {
    return 'var(--cc_color_link_subtle)';
  }
};

// z-index: 1 works most places, but 999 is the lowest to get Reports going
const FilterButtonPopover = styled(CCPopover)`
  padding: var(--cc_size_spacing_s);
  z-index: 9999;
`;

const Button = styled.button`
  ${baseButtonStyles}
  display: flex;
  align-items: center;
  flex: none;
  line-height: 40px;
  gap: var(--cc_size_spacing_xs);
  font-family: 'Averta', system-ui, sans-serif;

  ${(props) => {
    if (props.leftButton) {
      return `
        border-top-left-radius: var(--cc_size_border_radius_l);
        border-bottom-left-radius: var(--cc_size_border_radius_l);
      `;
    }
    if (props.rightButton) {
      return `
        border-top-right-radius: var(--cc_size_border_radius_l);
        border-bottom-right-radius: var(--cc_size_border_radius_l);
      `;
    }
    return `border-radius: var(--cc_size_border_radius_l);`;
  }};
  border-width: var(--cc_size_border_width_m);
  border-style: solid;
  border-color: ${(props) =>
    props.showFocusStyle
      ? 'var(--cc_color_link_secondary)'
      : 'var(--cc_color_border_input_inactive)'};
  height: 40px;
  padding: 0 ${tokens.size_spacing_s + 4}px;
  font-size: calc(var(--cc_size_text_s) + 1px);
  font-weight: 700;
  color: ${(props) => getColor(props.showFocusStyle, props.showActiveStyle)};
  background-color: ${(props) =>
    props.showActiveStyle
      ? 'var(--cc_color_button_background_secondary)'
      : 'var(--cc_color_background_1)'};
  transition: all cubic-bezier(0.785, 0.135, 0.15, 0.86) 0.2s;

  &:hover {
    border-color: ${(props) =>
      props.showActiveStyle
        ? 'var(--cc_color_button_background_secondary_darken_10)'
        : 'var(--cc_color_border_input_inactive_darken_10)'};
  }
`;

const Icon = styled.i`
  top: 0;
  font-size: 18px;
`;

const Badge = styled.div`
  text-align: center;
  font-size: var(--cc_size_text_xs);
  margin: 0 1px;
  width: 16px;
  height: 16px;
  line-height: 16px;
  border-radius: 50%;
  color: var(--cc_color_button_background_secondary);
  background-color: var(--cc_color_background_1);
`;
// #endregion

const FilterButtonWithPopover = ({
  children,
  isOpen,
  onOpen,
  isSelected,
  selectedCount,
  title,
  id,
  'data-testid': dataTestId,
  onClearClick,
  clearButtonDataTestId,
}) => {
  const renderRightContent = () => {
    if (selectedCount) {
      return <Badge>{`${selectedCount}`}</Badge>;
    } else {
      return <Icon className="mdi mdi-chevron-down" aria-hidden="true" />;
    }
  };

  return (
    <FilterButtonPopover
      id={id}
      mountNodeId="main"
      placement="bottom-start"
      isOpen={isOpen}
      onOpenChange={onOpen}
      flipOptions={{ fallbackPlacements: ['bottom'] }}
      trigger={
        <Button
          type="button"
          showFocusStyle={!!isOpen && !isSelected}
          showActiveStyle={!!isSelected}
          data-testid={dataTestId}
        >
          {title}
          {renderRightContent()}
        </Button>
      }
    >
      {children}
      {isSelected ? (
        <ClearButton
          onClick={onClearClick}
          dataTestId={clearButtonDataTestId}
        />
      ) : null}
    </FilterButtonPopover>
  );
};

FilterButtonWithPopover.propTypes = {
  children: PropTypes.node.isRequired,
  id: PropTypes.string,
  'data-testid': PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  onClearClick: PropTypes.func,
  onOpen: PropTypes.func.isRequired,
  selectedCount: PropTypes.number,
  title: PropTypes.string,
  clearButtonDataTestId: PropTypes.string,
};

FilterButtonWithPopover.defaultProps = {
  id: '',
  isSelected: false,
  onClearClick: () => {},
  selectedCount: 0,
  title: '',
  clearButtonDataTestId: '',
};

export default FilterButtonWithPopover;
